import { useLayoutEffect, useRef } from 'react';

// Preserve the visible history when prepending messages. Realtime arrivals only
// follow the bottom when the reader was already there, or sent the new message.
export function useConversationScroll(
  scope: string,
  items: { id: string; sender_id: string }[],
  accountID: string,
  loadOlder: () => Promise<void>,
) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const previous = useRef({ scope: '', first: '', last: '' });
  const history = useRef<{ scope: string; top: number; height: number } | null>(null);
  const first = items[0]?.id || '';
  const last = items.at(-1);
  useLayoutEffect(() => {
    const node = viewportRef.current;
    if (!node) return;
    const before = previous.current;
    if (before.scope !== scope) {
      history.current = null;
      atBottom.current = true;
      node.scrollTop = node.scrollHeight;
    } else if (history.current?.scope === scope && before.first !== first) {
      node.scrollTop = history.current.top + node.scrollHeight - history.current.height;
      history.current = null;
    } else if (before.last !== last?.id && (atBottom.current || last?.sender_id === accountID)) {
      node.scrollTop = node.scrollHeight;
    }
    previous.current = { scope, first, last: last?.id || '' };
  }, [scope, first, last?.id, accountID]);
  const onScroll = () => {
    const node = viewportRef.current;
    if (node) atBottom.current = node.scrollHeight - node.scrollTop - node.clientHeight < 80;
  };
  const loadEarlier = async () => {
    const node = viewportRef.current;
    const anchor = node ? { scope, top: node.scrollTop, height: node.scrollHeight } : null;
    history.current = anchor;
    await loadOlder();
    // A failed or empty response must not affect the next realtime update.
    requestAnimationFrame(() => {
      if (history.current === anchor) history.current = null;
    });
  };
  return { viewportRef, onScroll, loadEarlier };
}
